import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Calendar, Clock, Bookmark, BookmarkCheck, ThumbsUp, ThumbsDown,
  MessageSquare, Send, Loader2, AlertCircle,
} from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, CTAStrip } from './shared/blocks';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import SEO from './shared/SEO';
import { blogApi } from '../../services/blogApi';
import { trackEvent } from '../../lib/clarity';

// ──────────────────────────────────────────────────────────
//  Hooks
// ──────────────────────────────────────────────────────────
function useAuthed() {
  return Boolean(localStorage.getItem('sip_token'));
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso.slice(0, 10); }
}

// ──────────────────────────────────────────────────────────
//  Card
// ──────────────────────────────────────────────────────────
function BlogCard({ post }) {
  const readTime = post.readTimeMinutes ? `${post.readTimeMinutes} min` : post.readTime;
  const date = post.publishedAt ? formatDate(post.publishedAt) : post.date;
  const categoryLabel = post.categorySlug || post.category;
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="block rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
      data-testid={`blog-card-${post.slug}`}
    >
      {post.coverImage && (
        <img src={post.coverImage} alt={post.title} className="w-full h-40 object-cover rounded-lg mb-4" />
      )}
      <div className="text-xs uppercase tracking-wider text-mk-blue dark:text-mk-cyan mb-2 font-semibold">
        {categoryLabel}
      </div>
      <h3 className="font-bold leading-snug line-clamp-2">{post.title}</h3>
      <p className="text-sm text-mk-muted dark:text-slate-300 mt-2 leading-relaxed line-clamp-3">
        {post.excerpt}
      </p>
      <div className="mt-4 text-xs text-slate-500 flex items-center gap-3">
        {date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{date}</span>}
        {readTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{readTime}</span>}
      </div>
    </Link>
  );
}

// ──────────────────────────────────────────────────────────
//  Newsletter inline form
// ──────────────────────────────────────────────────────────
function NewsletterInline() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await blogApi.newsletter({ name, email, source: 'blog' });
      trackEvent('newsletter_subscribed', { source: 'blog' });
      toast.success('Subscribed. Watch your inbox.');
      setEmail(''); setName('');
    } catch (err) {
      toast.error(err.message || 'Subscription failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-mk-ice dark:bg-mk-navy p-6" data-testid="blog-newsletter-form">
      <h3 className="font-bold text-lg mb-1">Weekly notes for Indian teachers</h3>
      <p className="text-sm text-mk-muted dark:text-slate-300 mb-4">Short, practical, no spam.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-mk-midnight text-sm"
          data-testid="blog-newsletter-name"
        />
        <input
          type="email"
          placeholder="your.email@school.edu"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-mk-midnight text-sm"
          required
          data-testid="blog-newsletter-email"
        />
      </div>
      <Button type="submit" disabled={submitting} className="mt-3" data-testid="blog-newsletter-submit">
        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        Subscribe
      </Button>
    </form>
  );
}

// ──────────────────────────────────────────────────────────
//  Blog Index
// ──────────────────────────────────────────────────────────
export function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          blogApi.listPosts({ pageSize: 30 }),
          blogApi.listCategories(),
        ]);
        setPosts(p.items);
        setCats(c.items.map((x) => ({ slug: x.slug, label: x.name })));
      } catch (_e) {
        setPosts([]);
        setCats([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <MarketingLayout>
      <SEO
        title="ActanonEdu Blog | AI tools, School ERP & Multilingual Teaching"
        description="Field notes from Indian classrooms — AI tool for teachers, lesson plan generators, worksheet design, School ERP playbooks, and multilingual content strategy."
        path="/blog"
        keywords={['AI tool for teachers blog', 'school ERP blog', 'lesson plan generator', 'multilingual teaching']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }]}
      />
      <PageHero
        eyebrow="Knowledge Hub"
        title="Field notes from Indian classrooms"
        accent="and the AI that helps them."
        subtitle="Tactics for teachers, playbooks for principals, and honest research on what AI in education actually does (and doesn't) deliver."
        testid="blog-index-hero"
      />

      <Section>
        <div className="mb-8 flex flex-wrap gap-2" data-testid="blog-categories">
          <Link to="/blog">
            <Badge variant="outline" className="bg-mk-ice text-mk-deep border-mk-line hover:bg-mk-ice cursor-pointer">All</Badge>
          </Link>
          {cats.map((c) => (
            <Link key={c.slug} to={`/blog/category/${c.slug}`}>
              <Badge variant="outline" className="hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer">{c.label || c.name}</Badge>
            </Link>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
        )}

        <div className="mt-12">
          <NewsletterInline />
        </div>
      </Section>

      <CTAStrip
        title="Want more like this in your inbox?"
        subtitle="Weekly notes for the Indian teacher — short, practical, no spam."
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'WhatsApp', wa: 'Hi! I read the ActanonEdu blog.' }}
        testid="blog-final-cta"
      />
    </MarketingLayout>
  );
}

// ──────────────────────────────────────────────────────────
//  Category page
// ──────────────────────────────────────────────────────────
export function BlogCategoryPage() {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          blogApi.listPosts({ category: slug, pageSize: 30 }),
          blogApi.listCategories(),
        ]);
        setPosts(p.items);
        setCats(c.items);
      } catch (_e) {
        setPosts([]);
        setCats([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const cat = cats.find((c) => c.slug === slug);
  const label = cat?.label || cat?.name || slug;

  return (
    <MarketingLayout>
      <PageHero
        eyebrow={<><Link to="/blog" className="hover:underline">Blog</Link> · {label}</>}
        title={label}
        subtitle={`${posts.length} article${posts.length === 1 ? '' : 's'} in this category.`}
        testid="blog-category-hero"
      />
      <Section>
        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 mb-4">No articles in this category yet — but they&apos;re coming.</p>
            <Link to="/blog"><Button variant="outline">Back to all articles</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
        )}
      </Section>
    </MarketingLayout>
  );
}

// ──────────────────────────────────────────────────────────
//  Post page
// ──────────────────────────────────────────────────────────
export function BlogPostPage() {
  const { slug } = useParams();
  const authed = useAuthed();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          blogApi.getPost(slug),
          blogApi.getRelated(slug, 4).catch(() => ({ items: [] })),
        ]);
        setPost(p);
        setRelated(r.items);
      } catch (_e) {
        setPost(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <MarketingLayout>
        <Section>
          <div className="text-center py-20"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>
        </Section>
      </MarketingLayout>
    );
  }

  if (!post) {
    return (
      <MarketingLayout>
        <Section>
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold mb-3">Article not found</h1>
            <Link to="/blog"><Button>Back to blog</Button></Link>
          </div>
        </Section>
      </MarketingLayout>
    );
  }

  const date = post.publishedAt ? formatDate(post.publishedAt) : post.date;
  const readTime = post.readTimeMinutes ? `${post.readTimeMinutes} min` : post.readTime;
  const categorySlug = post.categorySlug || post.category;

  return (
    <MarketingLayout>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        keywords={post.keywords || [categorySlug, 'ActanonEdu', 'Indian teachers']}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
        article={{
          title: post.title,
          description: post.excerpt,
          datePublished: post.publishedAt || post.date,
          dateModified: post.updatedAt || post.publishedAt || post.date,
          author: post.author || 'ActanonEdu Team',
        }}
        type="article"
      />
      <PageHero
        eyebrow={<><Link to="/blog" className="hover:underline">Blog</Link> · <Link to={`/blog/category/${categorySlug}`} className="hover:underline">{categorySlug}</Link></>}
        title={post.title}
        subtitle={post.excerpt}
        testid="blog-post-hero"
      />
      <Section>
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-6 flex-wrap">
          {date && <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {date}</span>}
          {readTime && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {readTime} read</span>}
          {post.author && <span className="flex items-center gap-1.5">By {post.author}</span>}
        </div>

        {/* Cover image */}
        {post.coverImage && (
          <img src={post.coverImage} alt={post.title} className="w-full max-h-[420px] object-cover rounded-2xl mb-8" />
        )}

        {/* Body */}
        <article className="prose prose-slate dark:prose-invert max-w-3xl text-base leading-relaxed" data-testid="blog-post-body">
          <ReactMarkdown>{post.body || ''}</ReactMarkdown>
        </article>

        <div className="max-w-3xl mt-10 space-y-8">
          <PostActions post={post} authed={authed} />
          <CommentsSection postId={post.postId} authed={authed} />
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="text-xl font-bold mb-4">Related reading</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => <BlogCard key={r.slug} post={r} />)}
            </div>
          </div>
        )}
      </Section>
      <CTAStrip
        title="Want this content delivered weekly?"
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'WhatsApp', wa: `Hi! I read "${post.title}" on your blog.` }}
        testid="blog-post-final-cta"
      />
    </MarketingLayout>
  );
}

// ──────────────────────────────────────────────────────────
//  Post Actions (bookmark + rating)
// ──────────────────────────────────────────────────────────
function PostActions({ post, authed }) {
  const [bookmarked, setBookmarked] = useState(Boolean(post.bookmarked));
  const [rating, setRating] = useState({ helpful: 0, total: 0, my: null });

  useEffect(() => {
    if (!post.postId) return;
    blogApi.getRating(post.postId).then(setRating).catch(() => {});
  }, [post.postId]);

  const toggleBookmark = async () => {
    if (!authed) { toast.error('Please sign in to bookmark.'); return; }
    try {
      if (bookmarked) {
        await blogApi.unbookmark(post.postId);
        setBookmarked(false);
        toast.success('Removed from bookmarks.');
      } else {
        await blogApi.bookmark(post.postId);
        setBookmarked(true);
        toast.success('Bookmarked.');
      }
    } catch (e) {
      toast.error(e.message || 'Action failed.');
    }
  };

  const sendRating = async (helpful) => {
    if (!authed) { toast.error('Please sign in to rate.'); return; }
    try {
      const r = await blogApi.rate(post.postId, helpful);
      setRating({ helpful: r.helpful, total: r.total, my: helpful });
      toast.success('Thanks for the feedback.');
    } catch (e) {
      toast.error(e.message || 'Could not submit rating.');
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap border-t border-b border-mk-line dark:border-mk-edge py-4">
      <Button
        variant={bookmarked ? 'default' : 'outline'}
        size="sm"
        onClick={toggleBookmark}
        data-testid="blog-bookmark-btn"
      >
        {bookmarked ? <BookmarkCheck className="h-4 w-4 mr-1.5" /> : <Bookmark className="h-4 w-4 mr-1.5" />}
        {bookmarked ? 'Bookmarked' : 'Bookmark'}
      </Button>

      <div className="flex items-center gap-2 ml-auto text-sm">
        <span className="text-slate-500 mr-2">Was this helpful?</span>
        <Button
          variant={rating.my === true ? 'default' : 'outline'}
          size="sm"
          onClick={() => sendRating(true)}
          data-testid="blog-rating-helpful"
        >
          <ThumbsUp className="h-4 w-4" />
        </Button>
        <Button
          variant={rating.my === false ? 'default' : 'outline'}
          size="sm"
          onClick={() => sendRating(false)}
          data-testid="blog-rating-not-helpful"
        >
          <ThumbsDown className="h-4 w-4" />
        </Button>
        {rating.total > 0 && (
          <span className="text-xs text-slate-500 ml-2">
            {rating.helpful}/{rating.total} helpful
          </span>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
//  Comments (threaded, depth 2)
// ──────────────────────────────────────────────────────────
function CommentsSection({ postId, authed }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await blogApi.listComments(postId);
      setComments(data.items);
    } catch (_e) {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!authed) { toast.error('Please sign in to comment.'); return; }
    const trimmed = body.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const res = await blogApi.createComment(postId, trimmed, replyTo);
      if (res.status === 'rejected') {
        toast.error(res.reason || 'Comment did not pass moderation.');
      } else if (res.status === 'pending') {
        toast.success('Comment received — awaiting review.');
      } else {
        toast.success('Comment posted.');
      }
      setBody('');
      setReplyTo(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Could not post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  // Group by parent for threading
  const topLevel = comments.filter((c) => !c.parentId);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parentId) {
      (acc[c.parentId] = acc[c.parentId] || []).push(c);
    }
    return acc;
  }, {});

  return (
    <div data-testid="blog-comments-section">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <MessageSquare className="h-5 w-5" /> Comments ({comments.length})
      </h3>

      {/* Form */}
      <form onSubmit={submit} className="mb-8 rounded-xl border border-mk-line dark:border-mk-edge p-4 bg-mk-ice dark:bg-mk-navy">
        {!authed && (
          <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 mb-3">
            <AlertCircle className="h-4 w-4" />
            <span>Please <Link to="/login" className="underline font-medium">sign in</Link> to join the discussion.</span>
          </div>
        )}
        {replyTo && (
          <div className="text-xs text-slate-500 mb-2 flex items-center gap-2">
            Replying to a comment.
            <button type="button" onClick={() => setReplyTo(null)} className="underline">Cancel</button>
          </div>
        )}
        <Textarea
          placeholder={authed ? 'Share your experience or ask a question…' : 'Sign in to comment'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={!authed || submitting}
          rows={3}
          className="resize-none"
          data-testid="blog-comment-body"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-slate-500">
            Comments are moderated by Bosco Guardian. Be kind, be specific.
          </span>
          <Button
            type="submit"
            size="sm"
            disabled={!authed || !body.trim() || submitting}
            data-testid="blog-comment-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Post comment
          </Button>
        </div>
      </form>

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : topLevel.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No comments yet. Be the first to share your thoughts.</p>
      ) : (
        <div className="space-y-5">
          {topLevel.map((c) => (
            <CommentItem
              key={c.commentId}
              comment={c}
              replies={repliesByParent[c.commentId] || []}
              onReply={(id) => setReplyTo(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CommentItem({ comment, replies, onReply }) {
  return (
    <div className="rounded-xl border border-mk-line dark:border-mk-edge p-4 bg-white dark:bg-mk-midnight" data-testid={`blog-comment-${comment.commentId}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="font-semibold text-sm">{comment.authorName || 'Anonymous'}</div>
        <div className="text-xs text-slate-500">{formatDate(comment.createdAt)}</div>
      </div>
      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{comment.body}</p>
      <button
        onClick={() => onReply(comment.commentId)}
        className="mt-2 text-xs text-mk-blue hover:underline"
        data-testid={`blog-comment-reply-${comment.commentId}`}
      >
        Reply
      </button>

      {replies.length > 0 && (
        <div className="mt-4 pl-4 border-l-2 border-mk-line dark:border-mk-edge space-y-3">
          {replies.map((r) => (
            <div key={r.commentId} className="text-sm" data-testid={`blog-comment-${r.commentId}`}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="font-semibold">{r.authorName || 'Anonymous'}</div>
                <div className="text-xs text-slate-500">{formatDate(r.createdAt)}</div>
              </div>
              <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
